import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Heading,
  Container,
  Icon,
  InputGroup,
  InputLeftElement,
  Image,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { FaLock, FaShieldAlt } from 'react-icons/fa';
import { checkLockout, recordFailedAttempt, clearAttempts } from '../../utils/loginRateLimiter';

function AdminLogin() {
  const [secretCode, setSecretCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Check lockout before attempting login
    const lockout = checkLockout('admin');
    if (lockout.locked) {
      setError(`Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('secret_code', secretCode);
      if (error) {
        setError('Database error: ' + error.message);
      } else if (data.length === 1) {
        clearAttempts('admin');
        // Store only non-sensitive fields — never persist password or secret_code
        const { password, secret_code, ...safeData } = data[0];
        localStorage.setItem('adminUser', JSON.stringify(safeData));
        // Establish the Dialerr agent session cookie (best-effort — never blocks login).
        try {
          await fetch('/api/dialerr/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ secret_code: secretCode }),
          });
        } catch (e) { /* softphone sign-in is optional */ }
        navigate('/admin');
      } else {
        const result = recordFailedAttempt('admin');
        if (result.locked) {
          setError(`Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`);
        } else {
          setError(`Invalid code. ${result.attemptsRemaining} attempts remaining.`);
        }
      }
    } catch (err) {
      setError('Unexpected error: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" py={8}>
      <Container maxW="sm">
        <VStack spacing={6}>
          <VStack spacing={4}>
            <Image src="/logo.png" alt="AtHomeBloodDraw.com" maxW="200px" objectFit="contain" />
            <Box p={3} bg="red.500" borderRadius="full">
              <Icon as={FaShieldAlt} boxSize={6} color="white" />
            </Box>
            <Heading size="md" color="gray.700">Admin Portal</Heading>
            <Text color="gray.500" fontSize="sm">Authorized personnel only</Text>
          </VStack>

          <Box w="100%" bg="white" p={8} borderRadius="lg" boxShadow="md">
            <form onSubmit={handleLogin}>
              <VStack spacing={4}>
                {error && (
                  <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel fontSize="sm" color="gray.600">Access Code</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaLock} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      type="password"
                      value={secretCode}
                      onChange={(e) => setSecretCode(e.target.value)}
                      placeholder="Enter access code"
                      autoComplete="off"
                      size="lg"
                    />
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="red"
                  size="lg"
                  width="100%"
                  isLoading={isLoading}
                  loadingText="Verifying..."
                >
                  Sign In
                </Button>
              </VStack>
            </form>
          </Box>

          <Text fontSize="xs" color="gray.400" textAlign="center">
            AtHomeBloodDraw.com — Administration
          </Text>
        </VStack>
      </Container>
    </Box>
  );
}

export default AdminLogin;
